/**
 * provenance — строка «Профиль: …» плюс бейджи стадии/прогрессии сделки.
 * Зеркалит provenance-строку AnalysisPanel.tsx (§10, строки ~144-149),
 * StageBadge.tsx и ProgressionChip.tsx. Иконки lucide в печатном документе
 * недоступны — стрелки прогрессии уже вшиты в копирайт (`stageProgression.*`
 * содержит ↗/→/↘), поэтому чип не добавляет свою.
 */
import type { TFunction } from 'i18next';
import { getProfileDisplayName } from '@/features/prompt-builder/profileDisplay';
import { escapeHtml } from '../escape';
import type { SectionFn } from '../types';

export const renderProvenance: SectionFn = (input, t) => {
  const { record } = input;

  // The name the panel shows (#1109): a default profile in the reader's language.
  const profileName = input.promptProfileName
    ? getProfileDisplayName({ name: input.promptProfileName }, t as unknown as TFunction)
    : t('records.analysisPanel.provenance.baseProfile');
  const provenanceLine = `<p class="prov">${escapeHtml(
    `${t('records.analysisPanel.provenance.profile')}: ${profileName}`
  )}</p>`;

  const badges: string[] = [];

  // Бейдж стадии — скрыт целиком, если стадию не удалось разрешить (см.
  // StageBadge.tsx: пропавшее имя означает устаревший/неизвестный id, а не
  // «стадии нет»).
  if (input.crmStageName) {
    const showPipeline = !!input.crmMultiplePipelines && !!input.crmPipelineName;
    const label = showPipeline
      ? t('records.analysisPanel.stageBadge.labelWithPipeline', {
          stage: input.crmStageName,
          pipeline: input.crmPipelineName,
        })
      : t('records.analysisPanel.stageBadge.label', { stage: input.crmStageName });
    badges.push(`<span class="badge">${escapeHtml(label)}</span>`);
  }

  // Двойной гейт (ProgressionChip.tsx): крон штампует evaluatedAt с пустым
  // progression, когда запись стареет без изменения стадии — гейт только по
  // evaluatedAt обратился бы по пустому ключу progression.
  if (record.crmProgressionEvaluatedAt && record.crmStageProgression) {
    const progression = record.crmStageProgression;
    const variant =
      progression === 'advanced' ? ' up' : progression === 'regressed' ? ' down' : '';
    badges.push(
      `<span class="badge${variant}">${escapeHtml(
        t(`records.analysisPanel.stageProgression.${progression}`)
      )}</span>`
    );
  }

  const badgesBlock = badges.length ? `<div class="badges">${badges.join('')}</div>` : '';

  return provenanceLine + badgesBlock;
};
